import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ModActions from './ModActions';
import Post from './Post';
import { tokens } from '../tokens';
import api from '../api';

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return new Date(dateStr).toLocaleDateString();
}

export default function PostCard({ post, onDelete, showFullReplies = false, compact = false }) {
  const { user } = useAuth();
  const [replyContent, setReplyContent] = useState('');
  const [replies, setReplies] = useState(post.replies || []);
  const [submitting, setSubmitting] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const isAuthor = user._id === post.author;
  const isMod = user.role === 'moderator' || user.role === 'admin';

  // Map the post data to the shape Post expects
  const postDisplay = {
    alias: post.authorAlias,
    dept: post.authorDepartment,
    type: post.typeTag,
    body: post.content,
    timeAgo: timeAgo(post.createdAt),
    replies: replies.length,
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/posts/${post._id}/replies`, { content: replyContent });
      setReplies([...replies, data.reply]);
      setReplyContent(''); setShowReplyForm(false);
    } catch {}
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try { await api.delete(`/posts/${post._id}`); onDelete?.(post._id); } catch {}
  };

  const visibleReplies = showFullReplies ? replies : replies.slice(0, 3);
  const hiddenCount = replies.length - visibleReplies.length;

  return (
    <div>
      {/* Post display — uses new V2 Post component */}
      <Post post={postDisplay} tokens={tokens} compact={compact} />

      {/* Actions row — overlays onto the Post footer area */}
      {!compact && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          marginTop: -8, paddingBottom: 8,
        }}>
          {!post.locked && (
            <button
              className="gl-btn-bare"
              onClick={() => setShowReplyForm(!showReplyForm)}
              style={{
                fontFamily: tokens.fontMono, fontSize: 11,
                letterSpacing: '0.1em', color: tokens.textLow,
                transition: `color 200ms ${tokens.ease}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = tokens.textMid; }}
              onMouseLeave={e => { e.currentTarget.style.color = tokens.textLow; }}
            >Reply</button>
          )}

          {replies.length > 0 && (
            <Link to={`/post/${post._id}`} style={{
              fontFamily: tokens.fontMono, fontSize: 11,
              letterSpacing: '0.06em', color: tokens.textLow,
              textDecoration: 'none',
              transition: `color 200ms ${tokens.ease}`,
            }}
              onMouseEnter={e => { e.currentTarget.style.color = tokens.textMid; }}
              onMouseLeave={e => { e.currentTarget.style.color = tokens.textLow; }}
            >{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</Link>
          )}

          {post.locked && (
            <span style={{
              fontFamily: tokens.fontMono, fontSize: 10,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: tokens.danger, fontStyle: 'italic',
            }}>locked</span>
          )}

          {post.roleTag && post.roleTag !== 'General' && (
            <span style={{
              fontFamily: tokens.fontMono, fontSize: 10,
              letterSpacing: '0.1em', color: tokens.textDim,
            }}>{post.roleTag}</span>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMod && <ModActions post={post} onAction={() => onDelete?.(post._id)} />}
            {isAuthor && (
              <button
                className="gl-btn-bare"
                onClick={handleDelete}
                style={{
                  fontFamily: tokens.fontMono, fontSize: 10,
                  letterSpacing: '0.1em', color: tokens.textDim,
                  transition: `color 200ms ${tokens.ease}`,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = tokens.danger; }}
                onMouseLeave={e => { e.currentTarget.style.color = tokens.textDim; }}
              >delete</button>
            )}
          </div>
        </div>
      )}

      {/* Reply form */}
      {!compact && showReplyForm && !post.locked && (
        <div style={{ marginTop: 4, marginBottom: 12 }}>
          <form onSubmit={handleReply}>
            <textarea
              value={replyContent}
              onChange={e => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              rows={2}
              maxLength={5000}
              autoFocus
              style={{
                width: '100%',
                background: tokens.wing,
                border: `1px solid ${tokens.edge}`,
                borderRadius: 4,
                padding: '10px 14px',
                color: tokens.textHi,
                fontSize: 14,
                fontFamily: tokens.fontBody,
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'none',
                lineHeight: 1.65,
                transition: `border-color 200ms ${tokens.ease}`,
              }}
              onFocus={e => { e.currentTarget.style.borderColor = tokens.accent + '44'; }}
              onBlur={e => { e.currentTarget.style.borderColor = tokens.edge; }}
            />
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                className="gl-btn-bare"
                onClick={() => { setShowReplyForm(false); setReplyContent(''); }}
                style={{
                  fontFamily: tokens.fontMono, fontSize: 11,
                  letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: tokens.textLow, padding: '6px 12px',
                  transition: `color 200ms ${tokens.ease}`,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = tokens.textMid; }}
                onMouseLeave={e => { e.currentTarget.style.color = tokens.textLow; }}
              >Cancel</button>
              <button
                type="submit"
                className="gl-btn-bare"
                disabled={submitting || !replyContent.trim()}
                style={{
                  fontFamily: tokens.fontMono, fontSize: 11,
                  letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: (submitting || !replyContent.trim()) ? tokens.textDim : tokens.accent,
                  padding: '6px 16px',
                  opacity: (submitting || !replyContent.trim()) ? 0.5 : 1,
                  cursor: (submitting || !replyContent.trim()) ? 'not-allowed' : 'pointer',
                  transition: `all 200ms ${tokens.ease}`,
                }}
              >{submitting ? '...' : 'Reply'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Replies */}
      {!compact && visibleReplies.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {visibleReplies.map(reply => (
            <div key={reply._id} style={{
              paddingLeft: 16,
              borderLeft: `1px solid ${tokens.edge}`,
              marginBottom: 10,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{
                    fontFamily: tokens.fontDisplay,
                    fontSize: 13, fontStyle: 'italic',
                    color: tokens.textHi,
                    letterSpacing: tokens.displayTracking,
                  }}>{reply.authorAlias}</span>
                  <span style={{
                    fontFamily: tokens.fontMono, fontSize: 10,
                    letterSpacing: '0.06em', color: tokens.textLow,
                  }}>&middot; {timeAgo(reply.createdAt)}</span>
                </div>
                {isMod && <ModActions post={post} reply={reply} onAction={() => onDelete?.(post._id)} />}
              </div>
              <p style={{
                fontFamily: tokens.fontBody,
                fontSize: 14, lineHeight: 1.65,
                color: tokens.textMid, margin: 0,
                whiteSpace: 'pre-wrap',
              }}>{reply.content}</p>
            </div>
          ))}
          {hiddenCount > 0 && (
            <Link to={`/post/${post._id}`} style={{
              fontFamily: tokens.fontMono, fontSize: 11,
              letterSpacing: '0.06em', color: tokens.accent,
              textDecoration: 'none', paddingLeft: 16,
              opacity: 0.7,
              transition: `opacity 200ms ${tokens.ease}`,
            }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
            >View all {replies.length} replies</Link>
          )}
        </div>
      )}
    </div>
  );
}
